import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Elevation, ExteriorPalette } from '../../types';
import { useTheme } from '../styles/ThemeContext';
interface ExteriorGalleryProps {
  elevations: Elevation[];
  selectedElevationId: string | null;
  onSelectElevation: (elevation: Elevation) => void;
  palettes?: ExteriorPalette[];
  selectedPaletteId?: string | null;
  onSelectPalette?: (palette: ExteriorPalette) => void;
  modelName?: string;
}
type TabId = 'elevations' | 'palettes';
const getElevationPrice = (index: number): number => {
  if (index === 0) return 0;
  if (index <= 2) return 1500;
  if (index <= 4) return 3500;
  return 5500;
};
const getPalettePrice = (index: number): number => {
  if (index === 0) return 0;
  if (index <= 2) return 500;
  if (index <= 4) return 1200;
  return 2000;
};
const formatNumber = (n: number): string => n.toString().padStart(2, '0');
function MarqueeName({
  name,
  isDark
}: {
  name: string;
  isDark: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (containerRef.current && textRef.current) {
      const diff = textRef.current.scrollWidth - containerRef.current.clientWidth;
      setOverflow(diff > 2 ? diff + 8 : 0);
    }
  }, [name]);
  return <div ref={containerRef} className="overflow-hidden px-1" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-692">
      <span ref={textRef} className={`block whitespace-nowrap text-[9px] font-bold text-center uppercase tracking-wider ${isDark ? 'text-white/90' : 'text-gray-800'}`} style={{
      transform: isHovered && overflow > 0 ? `translateX(-${overflow}px)` : 'translateX(0)',
      transition: isHovered && overflow > 0 ? `transform ${Math.max(1, overflow / 40)}s ease-in-out` : 'transform 0.3s ease-out'
    }} title={name} data-id="element-693">
        {name}
      </span>
    </div>;
}
export function ExteriorGallery({
  elevations,
  selectedElevationId,
  onSelectElevation,
  palettes = [],
  selectedPaletteId,
  onSelectPalette,
  modelName
}: ExteriorGalleryProps) {
  const {
    isDark
  } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('elevations');
  const [search, setSearch] = useState('');
  const filteredElevations = elevations.filter(e => e.name.toLowerCase().includes(search.toLowerCase()));
  const filteredPalettes = palettes.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
  const tabs: {
    id: TabId;
    label: string;
    count: number;
    show: boolean;
  }[] = [{
    id: 'elevations',
    label: 'Elevations',
    count: elevations.length,
    show: true
  }, {
    id: 'palettes',
    label: 'Palettes',
    count: palettes.length,
    show: palettes.length > 0
  }];
  return <motion.div initial={{
    y: 100,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} exit={{
    y: 100,
    opacity: 0
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="absolute bottom-16 left-[260px] right-6 z-30 flex justify-center px-6" data-id="element-694">
      <div className={`w-full max-w-[1100px] rounded-2xl backdrop-blur-2xl border shadow-2xl overflow-hidden ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/85 border-white/40'}`} data-id="element-695">
        {/* Header */}
        <div className={`flex items-center justify-between gap-4 px-5 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200/60'}`} data-id="element-696">
          {/* Tabs */}
          <div className="flex items-center gap-1" data-id="element-697">
            {tabs.filter(t => t.show).map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isActive ? isDark ? 'bg-white/10 text-white' : 'bg-gray-900/5 text-gray-900' : isDark ? 'text-white/50 hover:text-white/80 hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-698">
                    <span className="uppercase tracking-wider" data-id="element-699">
                      {tab.label}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-mono ${isActive ? isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-teal-500/15 text-teal-700' : isDark ? 'bg-white/5 text-white/40' : 'bg-gray-100 text-gray-400'}`} data-id="element-700">
                      {tab.count}
                    </span>
                  </button>;
          })}
          </div>

          {/* Title / Model */}
          <div className="flex-1 flex items-center justify-center min-w-0" data-id="element-701">
            <div className="text-center" data-id="element-702">
              <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-703">
                Customize Exterior
              </div>
              {modelName && <div className={`text-xs font-semibold truncate ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-704">
                  {modelName}
                </div>}
            </div>
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-2" data-id="element-705">
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`} data-id="element-706">
              <Search className={`w-3 h-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-707" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className={`bg-transparent outline-none text-xs w-24 ${isDark ? 'text-white placeholder:text-white/30' : 'text-gray-900 placeholder:text-gray-400'}`} data-id="element-708" />
            </div>
            <button onClick={() => setCollapsed(c => !c)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`} title={collapsed ? 'Expand' : 'Collapse'} data-id="element-709">
              {collapsed ? <ChevronUp className="w-4 h-4" data-id="element-710" /> : <ChevronDown className="w-4 h-4" data-id="element-711" />}
            </button>
          </div>
        </div>

        {/* Body */}
        <AnimatePresence initial={false} data-id="element-712">
          {!collapsed && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} className="overflow-hidden" data-id="element-713">
              {activeTab === 'elevations' && <div className="px-5 py-4" data-id="element-714">
                  <div className="flex gap-3 overflow-x-auto scrollbar-thin" data-id="element-715">
                    {filteredElevations.length === 0 ? <div className={`w-full text-center py-8 text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-716">
                        No elevations match "{search}"
                      </div> : filteredElevations.map((elevation, index) => {
                const isSelected = selectedElevationId === elevation.id;
                const price = getElevationPrice(index);
                const isIncluded = price === 0;
                const formattedNum = formatNumber(index + 1);
                return <div key={elevation.id} className="group relative flex flex-col items-center gap-2 transition-all flex-shrink-0" data-id="element-717">
                            <button onClick={() => onSelectElevation(elevation)} className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-718">
                              <div className={`relative rounded-xl overflow-hidden border-2 transition-all duration-300 w-28 h-28 ${isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}`} data-id="element-719">
                                <img src={elevation.thumbnailUrl} alt={elevation.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-720" />

                                {/* Number badge top-left */}
                                <div className={`absolute top-1.5 left-1.5 z-10 min-w-[20px] h-5 flex items-center justify-center rounded-md px-1 backdrop-blur-md text-[10px] font-bold font-mono tabular-nums ${isDark ? 'bg-black/60 text-white/80 border border-white/20' : 'bg-white/80 text-gray-700 border border-gray-300/50'}`} data-id="element-721">
                                  {formattedNum}
                                </div>

                                {/* Name strip */}
                                <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-722">
                                  <MarqueeName name={elevation.name} isDark={isDark} data-id="element-723" />
                                </div>

                                {/* Selected overlay */}
                                {isSelected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-724">
                                    <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-725">
                                      <Check className="w-3 h-3 text-white" data-id="element-726" />
                                    </div>
                                  </div>}
                              </div>

                              {/* Price pill */}
                              <div className="flex flex-col items-center gap-0.5 w-full px-1" data-id="element-727">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isIncluded ? isDark ? 'text-white/30' : 'text-gray-400' : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}`} data-id="element-728">
                                  {isIncluded ? 'Included' : `+$${price.toLocaleString()}`}
                                </span>
                              </div>
                            </button>
                          </div>;
              })}
                  </div>
                </div>}

              {activeTab === 'palettes' && palettes.length > 0 && <div className="px-5 py-4" data-id="element-729">
                  <div className="flex gap-3 overflow-x-auto scrollbar-thin" data-id="element-730">
                    {filteredPalettes.length === 0 ? <div className={`w-full text-center py-8 text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-731">
                        No palettes match "{search}"
                      </div> : filteredPalettes.map((palette, index) => {
                const isSelected = selectedPaletteId === palette.id;
                const price = getPalettePrice(index);
                const isIncluded = price === 0;
                const formattedNum = formatNumber(index + 1);
                return <div key={palette.id} className="group relative flex flex-col items-center gap-2 transition-all flex-shrink-0" data-id="element-732">
                            <button onClick={() => onSelectPalette?.(palette)} className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-733">
                              <div className={`relative rounded-xl overflow-hidden border-2 transition-all duration-300 w-28 h-28 ${isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}`} data-id="element-734">
                                {/* Color stripes filling the thumbnail */}
                                <div className="absolute inset-0 flex" data-id="element-735">
                                  {palette.colors.map((color, idx) => <div key={idx} className="flex-1" style={{
                          backgroundColor: color
                        }} data-id="element-736" />)}
                                </div>

                                {/* Number badge */}
                                <div className={`absolute top-1.5 left-1.5 z-10 min-w-[20px] h-5 flex items-center justify-center rounded-md px-1 backdrop-blur-md text-[10px] font-bold font-mono tabular-nums ${isDark ? 'bg-black/60 text-white/80 border border-white/20' : 'bg-white/80 text-gray-700 border border-gray-300/50'}`} data-id="element-737">
                                  {formattedNum}
                                </div>

                                {/* Name strip */}
                                <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-738">
                                  <MarqueeName name={palette.name} isDark={isDark} data-id="element-739" />
                                </div>

                                {/* Selected overlay */}
                                {isSelected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-740">
                                    <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-741">
                                      <Check className="w-3 h-3 text-white" data-id="element-742" />
                                    </div>
                                  </div>}
                              </div>

                              {/* Price pill */}
                              <div className="flex flex-col items-center gap-0.5 w-full px-1" data-id="element-743">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isIncluded ? isDark ? 'text-white/30' : 'text-gray-400' : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}`} data-id="element-744">
                                  {isIncluded ? 'Included' : `+$${price.toLocaleString()}`}
                                </span>
                              </div>
                            </button>
                          </div>;
              })}
                  </div>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}